import * as React from "react";
import { useFormContext, Controller, type FieldValues, type FieldPath } from "react-hook-form";
import { cn } from "@/lib/cn";
import { LGoTextField } from "./LGoTextField";

export interface LGoFormFieldProps {
  name: string;
  label: React.ReactNode;
  description?: React.ReactNode;
  hint?: React.ReactNode;
  required?: boolean;
  type?: React.HTMLInputTypeAttribute;
  placeholder?: string;
  className?: string;
  /**
   * Custom control. When provided, replaces the default LGoTextField
   * but keeps label/error/description wrapper. The render prop receives
   * the RHF field state for binding to a custom input.
   */
  children?:
    | React.ReactNode
    | ((field: {
        value: unknown;
        onChange: (...args: unknown[]) => void;
        onBlur: () => void;
        name: string;
        error?: string;
      }) => React.ReactNode);
}

export function LGoFormField({
  name,
  label,
  description,
  hint,
  required,
  type,
  placeholder,
  className,
  children,
}: LGoFormFieldProps) {
  const ctx = useFormContext();
  if (!ctx) {
    throw new Error("LGoFormField must be used inside a <FormProvider>");
  }
  const error = ctx.formState.errors[name as FieldPath<FieldValues>];
  const errorMsg = error?.message ? String(error.message) : undefined;

  if (typeof children === "function") {
    return (
      <div data-field={name} className={cn("flex flex-col gap-1.5", className)}>
        <label className="text-sm font-medium text-foreground">
          {label}
          {required && <span className="text-destructive ml-0.5">*</span>}
        </label>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
        <Controller
          name={name}
          control={ctx.control}
          render={({ field, fieldState }) =>
            children({
              ...field,
              error: fieldState.error?.message,
            }) as React.ReactElement
          }
        />
        {errorMsg && <p className="text-xs text-destructive">{errorMsg}</p>}
        {!errorMsg && hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
    );
  }

  if (children) {
    return (
      <div data-field={name} className={cn("flex flex-col gap-1.5", className)}>
        <label className="text-sm font-medium text-foreground">
          {label}
          {required && <span className="text-destructive ml-0.5">*</span>}
        </label>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
        {children}
        {errorMsg && <p className="text-xs text-destructive">{errorMsg}</p>}
        {!errorMsg && hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
    );
  }

  return (
    <LGoTextField
      label={
        <>
          {label}
          {required && <span className="text-destructive ml-0.5">*</span>}
        </>
      }
      hint={hint}
      error={errorMsg}
      type={type}
      // Number inputs default to step=1, which makes any decimal value
      // (e.g. GPS coordinates) natively :invalid and silently blocks form
      // submit before RHF runs. Zod does the real validation, so allow any.
      step={type === "number" ? "any" : undefined}
      placeholder={placeholder}
      className={className}
      aria-required={required || undefined}
      data-field={name}
      {...ctx.register(name, { valueAsNumber: type === "number" || undefined })}
    />
  );
}
